import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { useBlogPostBySlug } from "@/lib/content";

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, loading } = useBlogPostBySlug(slug);

  if (loading && !post) {
    return (
      <section className="bg-white pt-20 pb-24 min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-400 text-sm">
          Loading…
        </div>
      </section>
    );
  }

  if (!post) {
    return (
      <section className="bg-white pt-20 pb-24 min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif text-4xl text-[#0a2540] mb-4">Post not found</h1>
          <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#0a2540] link-underline">
            <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" /> Back to all posts
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={post.metaTitle || post.title}
        description={post.metaDesc || post.excerpt}
        keywords={Array.isArray(post.keywords) ? post.keywords.join(", ") : post.keywords}
      />

      <article className="bg-white pt-20 pb-12 md:py-24" data-testid={`blog-post-${post.id}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to="/blog" className="text-xs uppercase tracking-[0.25em] text-[#0a2540]/70 link-underline inline-flex items-center gap-2 mb-6">
              <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden="true" /> Back to Blog
            </Link>
            {post.category && (
              <div className="inline-block bg-[#fffbea] text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full mb-5">
                {post.category}
              </div>
            )}
            <h1 className="font-serif text-4xl sm:text-5xl text-[#0a2540] leading-[1.1] mb-4">
              {post.title}
            </h1>
            {post.date && (
              <div className="text-xs text-slate-500 font-medium">{post.date}</div>
            )}
          </Reveal>
        </div>
      </article>

      {post.image && (
        <div className="bg-[#fafafa]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden bg-gray-100 rounded-2xl"
            >
              <img
                src={post.image}
                alt={post.title}
                loading="eager"
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      )}

      <section className="bg-white py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.content ? (
            <div
              className="prose prose-slate max-w-none font-sans text-base text-slate-700 leading-relaxed [&_h2]:font-serif [&_h2]:text-[#0a2540] [&_h3]:font-serif [&_h3]:text-[#0a2540] [&_a]:text-[#023e8a] [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          ) : (
            <p className="text-base text-slate-700 leading-relaxed">{post.excerpt}</p>
          )}
        </div>
      </section>

      <CTABanner />
    </>
  );
}
